import { Link, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import {
  LayoutDashboard,
  Users,
  CreditCard,
  TrendingUp,
  Receipt,
  Settings,
  Target,
  Activity,
  Inbox,
  HandCoins,
  Clock,
  ChevronDown,
  ChevronRight,
  Scale,
  List,
  Plug,
  Brain,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { ModeToggle } from './mode-toggle';
import { CompanySelector } from './company-selector';
import { SidebarToggle } from './sidebar-toggle';
import { useSidebar } from '@/contexts/sidebar-context';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface SubNavigationItem {
  name: string;
  href?: string;
  icon: any;
  badge?: string;
  isExpandable?: boolean;
  subItems?: {
    name: string;
    href: string;
    icon: any;
    badge?: string;
  }[];
}

interface NavigationItem {
  name: string;
  href?: string;
  icon: any;
  badge?: string;
  isExpandable?: boolean;
  subItems?: SubNavigationItem[];
}

const navigation: NavigationItem[] = [
  { name: 'Homepage', href: '/', icon: LayoutDashboard },
  { name: 'Customers', href: '/customers', icon: Users },
  { name: 'Transactions', href: '/transactions', icon: Receipt },
  { name: 'Payments', href: '/payments', icon: CreditCard },
  {
    name: 'Collections',
    icon: Target,
    isExpandable: true,
    subItems: [
      {
        name: 'Dashboard',
        href: '/collections/dashboard',
        icon: Activity,
      },
      {
        name: 'Collections Queue',
        href: '/collections/queue',
        icon: Inbox,
      },
      {
        name: 'Promise to Pay',
        href: '/collections/promises',
        icon: HandCoins,
      },
      {
        name: 'Ageing Analysis',
        href: '/collections/ageing-analysis',
        icon: TrendingUp,
      },
    ],
  },
  {
    name: 'Dispute Management',
    icon: Scale,
    isExpandable: true,
    subItems: [
      {
        name: 'Dashboard',
        href: '/disputes',
        icon: Activity,
      },
      {
        name: 'All Disputes',
        href: '/disputes/all',
        icon: List,
      },
    ],
  },
  { name: 'Credit Scoring', href: '/credit-scoring', icon: Brain },
  {
    name: 'Settings',
    icon: Settings,
    isExpandable: true,
    subItems: [
      {
        name: 'Company',
        href: '/settings/company',
        icon: LayoutDashboard,
      },
      {
        name: 'Users',
        href: '/users',
        icon: Users,
      },
      {
        name: 'Integrations',
        href: '/integrations',
        icon: Plug,
      },
      {
        name: 'Templates',
        href: '/settings/templates',
        icon: Receipt,
      },
      {
        name: 'Collection Strategies',
        href: '/settings/strategies',
        icon: Clock,
      },
      {
        name: 'Dispute Types',
        href: '/settings/dispute-types',
        icon: Scale,
      },
      {
        name: 'AI Configuration',
        href: '/settings/ai-configuration',
        icon: TrendingUp,
      },
    ],
  },
];

export function AppSidebar() {
  const location = useLocation();
  const { isCollapsed } = useSidebar();
  const [expandedItems, setExpandedItems] = useState<string[]>([]);

  // Auto-expand Settings when navigating to a settings route, users page, or integrations page
  // Auto-expand Dispute Management when navigating to any dispute route
  // Auto-expand Collections when navigating to any collections route
  useEffect(() => {
    const newExpanded: string[] = [];

    if (location.pathname.startsWith('/settings') || location.pathname.startsWith('/users') || location.pathname.startsWith('/integrations')) {
      newExpanded.push('Settings');
    }

    if (location.pathname.startsWith('/disputes')) {
      newExpanded.push('Dispute Management');
    }

    if (location.pathname.startsWith('/collections')) {
      newExpanded.push('Collections');
    }

    if (newExpanded.length > 0) {
      setExpandedItems((prev) => {
        const updated = [...prev];
        newExpanded.forEach(item => {
          if (!updated.includes(item)) {
            updated.push(item);
          }
        });
        return updated;
      });
    }
  }, [location.pathname]);

  const toggleExpanded = (name: string) => {
    setExpandedItems((prev) =>
      prev.includes(name) ? prev.filter((item) => item !== name) : [...prev, name]
    );
  };

  const isActive = (href: string) => {
    // For root path, exact match only
    if (href === '/') {
      return location.pathname === '/';
    }

    // Exact match
    if (location.pathname === href) {
      return true;
    }

    // For top-level menu items (not submenu items), allow matching child routes
    // This keeps "Customers" highlighted when viewing a customer detail page
    // But for submenu items within expandable sections, we want exact match only
    // to prevent the bug where both "Dashboard" and "All Disputes" are highlighted

    // Check if this is a submenu item by seeing if there's another menu item
    // that would be a more specific match
    const hasMoreSpecificMatch = navigation.some(item => {
      if (item.subItems) {
        return item.subItems.some(subItem =>
          subItem.href &&
          subItem.href !== href &&
          location.pathname === subItem.href
        );
      }
      return false;
    });

    // If there's a more specific submenu match, don't highlight this item
    if (hasMoreSpecificMatch) {
      return false;
    }

    // Otherwise, allow matching child routes for top-level items
    if (location.pathname.startsWith(href + '/')) {
      return true;
    }

    return false;
  };

  const isParentActive = (item: NavigationItem) => {
    if (!item.subItems) return false;
    return item.subItems.some((subItem) => isActive(subItem.href));
  };

  return (
    <TooltipProvider delayDuration={0}>
      <div
        className={cn(
          "border-r bg-card flex flex-col h-screen transition-all duration-300",
          isCollapsed ? "w-16" : "w-64"
        )}
      >
        <div className="flex h-16 items-center justify-between border-b px-6">
          {!isCollapsed && <h1 className="text-xl font-bold">Credit Intelligence</h1>}
          <SidebarToggle />
        </div>
        {!isCollapsed && <CompanySelector />}
        <nav className="space-y-1 p-4 flex-1 overflow-y-auto">
          {navigation.map((item) => {
            if (item.isExpandable && item.subItems) {
              const isExpanded = expandedItems.includes(item.name);
              const parentActive = isParentActive(item);

              return (
                <div key={item.name}>
                  {isCollapsed ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Link
                          to={item.subItems?.[0]?.href || '#'}
                          className={cn(
                            'flex w-full items-center justify-center rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                            parentActive
                              ? 'bg-primary/10 text-primary'
                              : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                          )}
                        >
                          <item.icon className="h-5 w-5" />
                        </Link>
                      </TooltipTrigger>
                      <TooltipContent side="right" className="font-normal">
                        {item.name}
                      </TooltipContent>
                    </Tooltip>
                  ) : (
                    <button
                      onClick={() => toggleExpanded(item.name)}
                      className={cn(
                        'flex w-full items-center justify-between gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                        parentActive
                          ? 'bg-primary/10 text-primary'
                          : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                      )}
                    >
                      <div className="flex items-center gap-3">
                        <item.icon className="h-5 w-5" />
                        {item.name}
                        {item.badge && (
                          <Badge variant="outline" className="h-5 px-1.5 text-xs border-destructive text-destructive">
                            {item.badge}
                          </Badge>
                        )}
                      </div>
                      {isExpanded ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                    </button>
                  )}
                  {isExpanded && !isCollapsed && (
                    <div className="ml-4 mt-1 space-y-1 border-l pl-4">
                      {item.subItems.map((subItem) => {
                        // Check if subItem is expandable (nested menu)
                        if (subItem.isExpandable && subItem.subItems) {
                          const isSubExpanded = expandedItems.includes(subItem.name);
                          const subParentActive = subItem.subItems.some((nestedItem) =>
                            nestedItem.href && isActive(nestedItem.href)
                          );

                          return (
                            <div key={subItem.name}>
                              <button
                                onClick={() => toggleExpanded(subItem.name)}
                                className={cn(
                                  'flex w-full items-center justify-between gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                                  subParentActive
                                    ? 'bg-primary/10 text-primary'
                                    : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                                )}
                              >
                                <div className="flex items-center gap-3">
                                  <subItem.icon className="h-4 w-4" />
                                  {subItem.name}
                                  {subItem.badge && (
                                    <Badge variant="outline" className="h-5 px-1.5 text-xs border-destructive text-destructive">
                                      {subItem.badge}
                                    </Badge>
                                  )}
                                </div>
                                {isSubExpanded ? (
                                  <ChevronDown className="h-3 w-3" />
                                ) : (
                                  <ChevronRight className="h-3 w-3" />
                                )}
                              </button>
                              {isSubExpanded && (
                                <div className="ml-4 mt-1 space-y-1 border-l pl-4">
                                  {subItem.subItems.map((nestedItem) => {
                                    const nestedItemActive = isActive(nestedItem.href);
                                    return (
                                      <Link
                                        key={nestedItem.name}
                                        to={nestedItem.href}
                                        className={cn(
                                          'flex items-center justify-between gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                                          nestedItemActive
                                            ? 'bg-primary text-primary-foreground'
                                            : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                                        )}
                                      >
                                        <div className="flex items-center gap-3">
                                          <nestedItem.icon className="h-3 w-3" />
                                          {nestedItem.name}
                                        </div>
                                        {nestedItem.badge && (
                                          <Badge variant="secondary" className="h-5 px-1.5 text-xs">
                                            {nestedItem.badge}
                                          </Badge>
                                        )}
                                      </Link>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        }

                        // Regular sub-item (not expandable)
                        const subItemActive = subItem.href ? isActive(subItem.href) : false;
                        return (
                          <Link
                            key={subItem.name}
                            to={subItem.href || '#'}
                            className={cn(
                              'flex items-center justify-between gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                              subItemActive
                                ? 'bg-primary text-primary-foreground'
                                : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                            )}
                          >
                            <div className="flex items-center gap-3">
                              <subItem.icon className="h-4 w-4" />
                              {subItem.name}
                            </div>
                            {subItem.badge && (
                              <Badge variant="secondary" className="h-5 px-1.5 text-xs">
                                {subItem.badge}
                              </Badge>
                            )}
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }

            // Regular menu item
            const itemActive = item.href ? isActive(item.href) : false;

            if (isCollapsed) {
              return (
                <Tooltip key={item.name}>
                  <TooltipTrigger asChild>
                    <Link
                      to={item.href || '#'}
                      className={cn(
                        'flex items-center justify-center rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                        itemActive
                          ? 'bg-primary text-primary-foreground'
                          : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                      )}
                    >
                      <item.icon className="h-5 w-5" />
                    </Link>
                  </TooltipTrigger>
                  <TooltipContent side="right" className="font-normal">
                    {item.name}
                  </TooltipContent>
                </Tooltip>
              );
            }

            return (
              <Link
                key={item.name}
                to={item.href || '#'}
                className={cn(
                  'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                  itemActive
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                )}
              >
                <item.icon className="h-5 w-5" />
                {item.name}
                {item.badge && (
                  <span className={cn(
                    "ml-auto px-2 py-0.5 text-xs font-medium rounded",
                    item.badge === 'Live'
                      ? "bg-green-500 text-white"
                      : "bg-secondary text-secondary-foreground"
                  )}>
                    {item.badge}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>
        {!isCollapsed && <ModeToggle />}
      </div>
    </TooltipProvider>
  );
}
